import React, { useEffect, useState } from 'react';
import { useStyletron } from '../../Themes';
import { Button } from '../Button';
import { Link } from '../Link';
import { StatefulPopover } from '../Popover';
import { Tooltip } from '../Tooltip';
import { breadcrumbsHomeIconStyle, breadcrumbsLastItemStyle } from './styles';
import { IItems as _items, ISmartProps } from './types';
import { View } from './view';

export type IItems = _items;
export type BreadcrumbsProps = Omit<ISmartProps, 'css'>;

export const Breadcrumbs = React.forwardRef((props: BreadcrumbsProps, ref: any) => {
	const { items: initialJsonItems, children = [], uncollapsedItems = 2, ...rest } = props;

	const [displayedItems, updateDisplayedItems] = useState<any>([]);
	const [css, theme]: any = useStyletron();

	const onClickExpand = () => {
		if (items) {
			updateDisplayedItems(items);
		}
		return false; // prevent href
	};

	const items =
		initialJsonItems ||
		React.useMemo(() => {
			return React.Children.map(children, ({ props = {} }: any) => {
				return {
					...props,
					label: props.children,
				};
			});
		}, [children]);

	const itemsBeforeEllipsis: IItems[] = React.useMemo(() => {
		return uncollapsedItems > (items?.length || 0) / 2
			? items?.slice(0, (items?.length - 1) / 2) || []
			: items?.slice(0, uncollapsedItems) || [];
	}, [items, uncollapsedItems]);

	const itemsAfterEllipsis: IItems[] = React.useMemo(() => {
		return uncollapsedItems > (items?.length || 0) / 2
			? items?.slice((items?.length - 1) / 2) || []
			: items?.slice(items.length - uncollapsedItems) || [];
	}, [items, uncollapsedItems]);

	useEffect(() => {
		let newItems: any = [];
		if (props.collapsePopover) {
			newItems = props.collapse
				? [...itemsBeforeEllipsis, { ellipsisPopover: true }, ...itemsAfterEllipsis]
				: items;
		} else {
			newItems = props.collapse ? [...itemsBeforeEllipsis, { ellipsis: true }, ...itemsAfterEllipsis] : items;
		}

		updateDisplayedItems(newItems);
	}, [props.collapse, items, itemsBeforeEllipsis, itemsAfterEllipsis, props.collapsePopover]);

	return (
		<View {...rest} ref={ref}>
			{displayedItems?.map((item: any, index: number) => {
				const { home, ...itemRest } = item;
				const key = `${item.label}_${index}`;

				if (item.ellipsis) {
					return (
						<Link key={key} role="button" onClick={onClickExpand}>
							...
						</Link>
					);
				}
				if (item.ellipsisPopover) {
					// todo-a11y: add tooltip with text equal aria-label
					return (
						<StatefulPopover
							content={<Breadcrumbs items={initialJsonItems}></Breadcrumbs>}
							placement="bottomLeft"
							inverted
						>
							<Button
								aria-label="Expand breadcrumb"
								key={key}
								theme="tertiary-phantom"
								iconBefore="more_horiz"
								size="compact"
							/>
						</StatefulPopover>
					);
				}
				if (item.home || item.iconBefore === 'home') {
					return (
						<Tooltip content="Homepage" placement="bottomLeft">
							<Link
								{...itemRest}
								key={key}
								iconBefore={'home'}
								className={css(breadcrumbsHomeIconStyle(theme))}
								aria-label="Homepage"
							/>
						</Tooltip>
					);
				}
				if (displayedItems && index === displayedItems.length - 1) {
					return (
						<span key={key} className={css(breadcrumbsLastItemStyle())}>
							{item.label}
						</span>
					);
				}
				if (item.href) {
					return (
						<Link key={key} iconBefore={item.icon} {...item}>
							{item.label}
						</Link>
					);
				}
				return (
					<Link key={key} href="#">
						{item.label}
					</Link>
				);
			})}
		</View>
	);
});

export const BreadcrumbsResponsive = React.forwardRef((props: ISmartProps, ref: any) => {
	const { items: initialJsonItems, children = [], uncollapsedItems = 2, ...rest } = props;

	const [displayedItems, updateDisplayedItems] = useState<any>([]);
	const [css, theme]: any = useStyletron();

	const items =
		initialJsonItems ||
		React.useMemo(() => {
			return React.Children.map(children, ({ props = {} }: any) => {
				return {
					...props,
					label: props.children,
				};
			});
		}, [children]);

	const itemsBeforeEllipsis: IItems[] = React.useMemo(() => {
		return uncollapsedItems > (items?.length || 0) / 2
			? items?.slice(0, (items?.length - 1) / 2) || []
			: items?.slice(0, uncollapsedItems) || [];
	}, [items, uncollapsedItems]);

	const itemsAfterEllipsis: IItems[] = React.useMemo(() => {
		return uncollapsedItems > (items?.length || 0) / 2
			? items?.slice((items?.length - 1) / 2) || []
			: items?.slice(items.length - uncollapsedItems) || [];
	}, [items, uncollapsedItems]);

	useEffect(() => {
		const newItems = props.collapse
			? [/* ...itemsBeforeEllipsis,  */ { ellipsis: true }, ...itemsAfterEllipsis]
			: items;

		updateDisplayedItems(newItems);
	}, [props.collapse, items, itemsBeforeEllipsis, itemsAfterEllipsis]);

	return (
		<View {...rest} ref={ref}>
			{displayedItems?.map((item: any, index: number) => {
				const { home, ...itemRest } = item;
				const key = `${item.label}_${index}`;

				if (item.ellipsis) {
					// todo-a11y: add tooltip with text equal aria-label
					return (
						<StatefulPopover
							content={<Breadcrumbs items={initialJsonItems}></Breadcrumbs>}
							placement="bottomLeft"
							inverted
						>
							<Button
								aria-label="Expand breadcrumb"
								key={key}
								theme="tertiary"
								iconBefore="more_horiz"
								size="compact"
								data-testid="epo-breadcrumb-collapsebtn"
							/>
						</StatefulPopover>
					);
				}
				if (item.home || item.iconBefore === 'home') {
					return (
						<Tooltip content="Homepage" placement="bottomLeft">
							<Link
								{...itemRest}
								key={key}
								iconBefore={'home'}
								className={css(breadcrumbsHomeIconStyle(theme))}
								aria-label="Homepage"
							/>
						</Tooltip>
					);
				}
				if (displayedItems && index === displayedItems.length - 1) {
					return (
						<span key={key} className={css(breadcrumbsLastItemStyle())}>
							{item.label}
						</span>
					);
				}
				if (item.href) {
					return (
						<Link key={key} iconBefore={item.icon} {...item}>
							{item.label}
						</Link>
					);
				}
				return (
					<Link key={key} href="#">
						{item.label}
					</Link>
				);
			})}
		</View>
	);
});
